import { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import DateTextPicker from '../components/DateTextPicker';
import './Customers.css';
import './EmployeePerformance.css';

function firstOfYear() { return `${new Date().getFullYear()}-01-01`; }
function today() { return new Date().toISOString().slice(0, 10); }

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const METRICS = [
  { key: 'billed', label: 'Billed', kind: 'money' },
  { key: 'hours', label: 'Hours', kind: 'num' },
  { key: 'days_worked', label: 'Days Wkd', kind: 'num' },
  { key: 'days_leave', label: 'Leave', kind: 'num' },
  { key: 'days_unassigned', label: 'Unasgn', kind: 'num' },
  { key: 'ctc', label: 'CTC', kind: 'money' },
  { key: 'turnaround', label: 'T/A', kind: 'ratio' },
  { key: 'attendance_pct', label: 'Att%', kind: 'pct' },
];

function turnaroundClass(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (n >= 1.5) return 'ep-perf-ta-good';
  if (n < 1) return 'ep-perf-ta-bad';
  return 'ep-perf-ta-warn';
}

function attendanceClass(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (n >= 90) return 'ep-perf-att-good';
  if (n < 70) return 'ep-perf-att-bad';
  return 'ep-perf-att-warn';
}

export default function EmployeePerformance() {
  const [from, setFrom] = useState(firstOfYear());
  const [to, setTo] = useState(today());
  const [employees, setEmployees] = useState([]);
  const [selEmps, setSelEmps] = useState([]);
  const [data, setData] = useState({ months: [], rows: [], working_days: {}, has_sub_brand: false });
  const [shownMetrics, setShownMetrics] = useState(['billed', 'hours', 'days_worked', 'turnaround', 'attendance_pct']);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);

  useEffect(() => { api.get('/employees').then(setEmployees).catch(() => {}); }, []);

  const run = () => {
    setLoading(true); setErr('');
    setPage(1);
    const qs = new URLSearchParams({ from, to });
    if (selEmps.length) qs.append('emp_ids', selEmps.join(','));
    api.get(`/reports/employee-performance?${qs.toString()}`)
      .then(d => setData(d || { months: [], rows: [] }))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = e => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  useEffect(() => { setPage(1); }, [pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(data.rows.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [data.rows.length, pageSize]);

  const fmt = (v, kind) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (kind === 'money') return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (kind === 'pct') return `${n.toFixed(1)}%`;
    if (kind === 'ratio') return n.toFixed(2);
    return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  const toggleMetric = k => {
    setShownMetrics(prev => {
      if (prev.includes(k) && prev.length <= 1) return prev;
      return prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k];
    });
  };

  const visibleMetrics = useMemo(() => METRICS.filter(m => shownMetrics.includes(m.key)), [shownMetrics]);

  const employeeFilterOptions = useMemo(
    () => employees.map(e => ({ value: e.emp_id, label: `${e.emp_id} · ${e.full_name || ''}` })),
    [employees]
  );

  const totalRows = data.rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = data.rows.slice(offset, offset + pageSize);
  const rangeStart = totalRows === 0 ? 0 : offset + 1;
  const rangeEnd = totalRows === 0 ? 0 : Math.min(offset + pageSize, totalRows);

  const baseCols = 5 + (data.has_sub_brand ? 1 : 0);
  const emptyColSpan = baseCols + data.months.length * visibleMetrics.length;

  return (
    <>
      <div className="page-header">
        <h1>Employee Performance Report</h1>
      </div>
      <div className="page-content ep-perf-page">
        <div className="customers-top ep-perf-stats-top">
          <div className="customers-stats" aria-label="Report summary">
            <div className="customers-stat">
              <div className="customers-stat-label">Date range</div>
              <div className="customers-stat-value tabular-nums">
                {loading ? '—' : `${from} → ${to}`}
              </div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Rows</div>
              <div className="customers-stat-value tabular-nums">{loading ? '—' : totalRows.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Employees in filter</div>
              <div className="customers-stat-value tabular-nums">
                {selEmps.length ? selEmps.length.toLocaleString() : 'All'}
              </div>
            </div>
          </div>
        </div>

        <div className="card ep-perf-filters-card">
          <details className="ep-perf-filters-details" open>
            <summary className="ep-perf-filters-summary">
              <span className="ep-perf-filters-chevron" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="ep-perf-filters-summary-title">Filters</span>
              <span className="ep-perf-filters-summary-hint tabular-nums">
                {from} → {to}
              </span>
              {selEmps.length > 0 ? (
                <span className="ep-perf-filters-scope-badge">{selEmps.length}</span>
              ) : null}
            </summary>
            <div className="ep-perf-filters-details-body">
              <div className="form-grid ep-perf-filter-grid">
                <DateTextPicker id="ep-perf-from" label="From date" value={from} onChange={setFrom} />
                <DateTextPicker id="ep-perf-to" label="To date" value={to} onChange={setTo} />
                <div className="form-group ep-perf-filter-dropdown">
                  <MultiSelectDropdown
                    id="ep-perf-employees"
                    label="Employees"
                    placeholder="All employees"
                    options={employeeFilterOptions}
                    value={selEmps}
                    onChange={setSelEmps}
                  />
                </div>
              </div>
              <div className="ep-perf-filter-actions">
                <button type="button" className="btn btn-primary" onClick={run} disabled={loading}>
                  {loading ? 'Loading…' : 'Run report'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setSelEmps([])}>
                  Clear filters
                </button>
              </div>
              <div className="ep-perf-metrics-block">
                <p className="ep-perf-metrics-title">Visible metrics</p>
                <div className="ep-perf-metrics-grid">
                  {METRICS.map(m => (
                    <label key={m.key} className="ep-perf-metric-label">
                      <input
                        type="checkbox"
                        checked={shownMetrics.includes(m.key)}
                        onChange={() => toggleMetric(m.key)}
                      />
                      <span className="ep-perf-metric-text">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </details>
        </div>

        {err ? (
          <div className="ep-perf-error" role="alert">
            {err}
          </div>
        ) : null}

        <div className="card ep-perf-results-card">
          <div className="card-header ep-perf-results-head">
            <span>
              Results
              <span className="ep-perf-results-meta-muted">
                {' · '}
                {loading ? '…' : `${totalRows} row${totalRows === 1 ? '' : 's'}`}
                {visibleMetrics.length ? ` · ${visibleMetrics.length} metric${visibleMetrics.length === 1 ? '' : 's'}/month` : ''}
              </span>
            </span>
          </div>
          <p className="ep-perf-hint">
            <strong>T/A (turnaround)</strong> = billed ÷ monthly CTC (green ≥ 1.5, amber 1.0–1.49, red &lt; 1.0).
            <strong> Att%</strong> = days worked ÷ working weekdays in month (green ≥ 90%, red &lt; 70%).
          </p>
          <div className="ep-perf-table-frame">
            <div className="table-container ep-perf-table-wrap">
              <table className="ep-perf-table">
                <caption className="ep-perf-table-caption">
                  Employee performance by month and metric
                </caption>
                <thead>
                  <tr>
                    <th rowSpan={2} scope="col" className="ep-perf-th ep-perf-sticky-col-1">Emp ID</th>
                    <th rowSpan={2} scope="col" className="ep-perf-th ep-perf-sticky-col-2">Employee</th>
                    <th rowSpan={2} scope="col" className="ep-perf-th">Customer</th>
                    {data.has_sub_brand ? <th rowSpan={2} scope="col" className="ep-perf-th">Sub-brand</th> : null}
                    <th rowSpan={2} scope="col" className="ep-perf-th">RFP</th>
                    <th rowSpan={2} scope="col" className="ep-perf-th text-right">CTC</th>
                    {data.months.map(m => (
                      <th
                        key={m}
                        colSpan={visibleMetrics.length}
                        scope="colgroup"
                        className="ep-perf-th ep-perf-th-month-group"
                      >
                        {m}{' '}
                        <span className="ep-perf-th-wd">
                          (WD: {data.working_days?.[m] ?? 0})
                        </span>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {data.months.map(m =>
                      visibleMetrics.map((metric, mi) => (
                        <th
                          key={`${m}-${metric.key}`}
                          scope="col"
                          className={`ep-perf-th ep-perf-th-metric text-right${mi === 0 ? ' ep-perf-th-metric-first' : ''}`}
                        >
                          {metric.label}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, i) => (
                    <tr key={`${r.emp_id}-${r.rfp_num}-${r.customer_name}-${offset + i}`} className="ep-perf-body-row">
                      <td className="ep-perf-td tabular-nums ep-perf-sticky-col-1">{r.emp_id || '—'}</td>
                      <td className="ep-perf-td ep-perf-sticky-col-2" title={r.emp_name || ''}>{r.emp_name || '—'}</td>
                      <td className="ep-perf-td">{r.customer_name || '—'}</td>
                      {data.has_sub_brand ? <td className="ep-perf-td">{r.sub_brand || '—'}</td> : null}
                      <td className="ep-perf-td">{r.rfp_num || '—'}</td>
                      <td className="ep-perf-td text-right tabular-nums">{fmt(r.monthly_ctc, 'money')}</td>
                      {data.months.map(m => {
                        const cell = r.metrics?.[m] || {};
                        return visibleMetrics.map((metric, mi) => {
                          const extra =
                            metric.key === 'turnaround'
                              ? turnaroundClass(cell.turnaround)
                              : metric.key === 'attendance_pct'
                                ? attendanceClass(cell.attendance_pct)
                                : '';
                          return (
                            <td
                              key={`${m}-${metric.key}`}
                              className={`ep-perf-td ep-perf-td-metric${mi === 0 ? ' ep-perf-td-metric-first' : ''} ${extra}`.trim()}
                            >
                              {fmt(cell[metric.key], metric.kind)}
                            </td>
                          );
                        });
                      })}
                    </tr>
                  ))}
                  {data.rows.length === 0 ? (
                    <tr className="ep-perf-empty-row">
                      <td colSpan={emptyColSpan}>{loading ? 'Loading…' : 'No data for the chosen filters.'}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {!loading && totalRows > 0 ? (
              <div className="ep-perf-pager" role="navigation" aria-label="Results table pagination">
                <div className="ep-perf-pager-summary">
                  <span className="ep-perf-pager-summary-label">Showing</span>
                  <span className="ep-perf-pager-summary-range">
                    <strong className="tabular-nums">{rangeStart}</strong>
                    <span className="ep-perf-pager-summary-dash">–</span>
                    <strong className="tabular-nums">{rangeEnd}</strong>
                    <span className="ep-perf-pager-summary-of">of</span>
                    <strong className="tabular-nums">{totalRows.toLocaleString()}</strong>
                    <span className="ep-perf-pager-summary-unit">rows</span>
                  </span>
                </div>
                <div className="ep-perf-pager-controls">
                  <div className="ep-perf-pager-page-size" ref={pageSizeDdRef}>
                    <span className="ep-perf-pager-field-label" id="ep-perf-page-size-label">
                      Rows per page
                    </span>
                    <div className="ep-perf-pager-dd">
                      <button
                        type="button"
                        className={`ep-perf-pager-dd-trigger ${pageSizeOpen ? 'ep-perf-pager-dd-trigger-open' : ''}`}
                        aria-haspopup="listbox"
                        aria-expanded={pageSizeOpen}
                        aria-labelledby="ep-perf-page-size-label"
                        aria-label={`Rows per page, current value ${pageSize}`}
                        onClick={() => setPageSizeOpen(o => !o)}
                      >
                        <span className="tabular-nums">{pageSize}</span>
                        <span className="ep-perf-pager-dd-chevron" aria-hidden>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </span>
                      </button>
                      {pageSizeOpen ? (
                        <ul className="ep-perf-pager-dd-menu" role="listbox" aria-labelledby="ep-perf-page-size-label">
                          {PAGE_SIZE_OPTIONS.map(n => (
                            <li
                              key={n}
                              role="option"
                              aria-selected={pageSize === n}
                              className={
                                pageSize === n
                                  ? 'ep-perf-pager-dd-option ep-perf-pager-dd-option-active'
                                  : 'ep-perf-pager-dd-option'
                              }
                              onMouseDown={e => {
                                e.preventDefault();
                                setPageSize(n);
                                setPageSizeOpen(false);
                              }}
                            >
                              {n}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                  <div className="ep-perf-pager-nav" role="group" aria-label="Page navigation">
                    <button
                      type="button"
                      className="ep-perf-pager-step"
                      disabled={safePage <= 1}
                      onClick={() => setPage(1)}
                      aria-label="First page"
                      title="First page"
                    >
                      <span className="ep-perf-pager-step-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 19l-7-7 7-7" />
                          <path d="M18 19l-7-7 7-7" />
                        </svg>
                      </span>
                      <span>First</span>
                    </button>
                    <button
                      type="button"
                      className="ep-perf-pager-step"
                      disabled={safePage <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      aria-label="Previous page"
                      title="Previous page"
                    >
                      <span className="ep-perf-pager-step-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M15 18l-6-6 6-6" />
                        </svg>
                      </span>
                      <span>Prev</span>
                    </button>
                    <div className="ep-perf-pager-chip">
                      <span className="ep-perf-pager-chip-label">Page</span>
                      <span className="ep-perf-pager-chip-current tabular-nums">{safePage}</span>
                      <span className="ep-perf-pager-chip-sep">/</span>
                      <span className="ep-perf-pager-chip-total tabular-nums">{totalPages}</span>
                    </div>
                    <button
                      type="button"
                      className="ep-perf-pager-step"
                      disabled={safePage >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      aria-label="Next page"
                      title="Next page"
                    >
                      <span>Next</span>
                      <span className="ep-perf-pager-step-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="ep-perf-pager-step"
                      disabled={safePage >= totalPages}
                      onClick={() => setPage(totalPages)}
                      aria-label="Last page"
                      title="Last page"
                    >
                      <span>Last</span>
                      <span className="ep-perf-pager-step-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 5l7 7-7 7" />
                          <path d="M6 5l7 7-7 7" />
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
